import React, { Component } from "react";

// Externals
import PropTypes from "prop-types";

// Material helpers
import { withStyles } from "@material-ui/core";

// Material components
import {
  Grid,
  Typography,
  Select,
  IconButton,
  TextField,
  Button,
} from "@material-ui/core";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";

import { Refresh as RefreshIcon } from "@material-ui/icons";

// Shared layouts
import { Dashboard as DashboardLayout } from "layouts";

// Custom components
import { TrendChart, ImpressionChart, Impressions } from "./components";

import compose from "recompose/compose";
import { connect } from "react-redux";
import { getCids } from "../../actions/authActions";
import api from "../../api";

import SelectBar from "react-select";
import { groupBy } from "underscore";

// Component styles
const styles = (theme) => ({
  root: {
    padding: theme.spacing.unit * 4,
  },
  item: {
    height: "100%",
  },
});

const colourStyles = {
  control: (styles) => ({ ...styles, fontFamily: "Roboto" }),
  option: (styles) => ({ ...styles, fontFamily: "Roboto" }),
};

const timeRangeList = [
  "Last Hour",
  "Last 24 Hours",
  "Last 7 Days",
  "Last 30 Days",
  "Previous Month",
  "This Month",
  "Since a date",
];

const GroupByList = [
  "Total Sessions",
  "Session Durations",
  "Source Frames",
  "Source Durations",
  "Max vCPU",
  "Transcoded Frames",
  "Transcoded Durations",
];

const flavorOptions = [
  { value: "VOD", label: "VOD" },
  { value: "Live", label: "Live" },
  { value: "Fidelity", label: "Fidelity" },
  { value: "Optimizer", label: "Optimizer" },
];

const colors = [
  "#E12B17",
  "#E1AA17",
  "#CDF00E",
  "#17EEB3",
  "#39839B",
  "#2851C8",
  "#9719C0",
  "#D121A9",
  "#F31A20",
  "#5D6545",
];

class Dashboard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedOption: [],
      cidOption: [],
      timeRange: 0,
      lastUpdated: new Date(),
      data: {
        labels: [],
        datasets: [],
        summaryText: "",
      },
      groupBy: 0,
      kind: "session",
      disableKindSwitch: false,
      errorMessage: "",
      selectedFlavor: "VOD",
      sinceDate: "",
    };
  }
  getBucketSizeSec(timeRange) {
    return timeRange === 0 ? 300 : 3600;
  }

  alignTs(ts, step) {
    return Math.floor(ts / step) * step;
  }

  getTS(timeRange, time) {
    const date = new Date(time);

    switch (timeRange) {
      case 0: {
        // Last Hour
        const ts = Math.floor(date.getTime() / 1000);
        return Math.floor(ts / 300) * 300 - 11 * 300;
      }

      case 1: {
        // Last 24 Hours
        date.setHours(date.getHours() - 24);
        date.setUTCMinutes(0, 0, 0);
        return Math.floor(date.getTime() / 1000);
      }

      case 2: {
        // Last 7 Days
        date.setHours(date.getHours() - 168);
        date.setUTCMinutes(0, 0, 0);
        return Math.floor(date.getTime() / 1000);
      }

      case 3: {
        // Last 30 Days
        date.setHours(date.getHours() - 720);
        date.setUTCMinutes(0, 0, 0);
        return Math.floor(date.getTime() / 1000);
      }

      case 4: {
        // Previous Month
        const prevMonthStart = new Date(
          Date.UTC(
            date.getUTCFullYear(),
            date.getUTCMonth() - 1,
            1,
            0,
            0,
            0,
            0,
          ),
        );
        return Math.floor(prevMonthStart.getTime() / 1000);
      }

      case 5: {
        // This Month
        const thisMonthStart = new Date(
          Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 0, 0, 0, 0),
        );
        return Math.floor(thisMonthStart.getTime() / 1000);
      }

      case 6: {
        // Since a date
        if (!this.state.sinceDate) return undefined;
        const sinceDate = new Date(`${this.state.sinceDate}T00:00:00Z`);
        return Math.floor(sinceDate.getTime() / 1000);
      }

      default:
        return undefined;
    }
  }

  getLabelEpochs(timeRange, lastUpdated) {
    const step = this.getBucketSizeSec(timeRange);
    const startTs = this.getTS(timeRange, lastUpdated);

    if (typeof startTs !== "number") {
      return [];
    }

    let endTs;
    const nowTs = Math.floor(new Date(lastUpdated).getTime() / 1000);

    if (timeRange === 0) {
      endTs = Math.floor(nowTs / 300) * 300;
    } else {
      endTs = Math.floor(nowTs / 3600) * 3600;
    }

    const epochs = [];
    for (let ts = startTs; ts <= endTs; ts += step) {
      epochs.push(ts);
    }

    return epochs;
  }

  formatDurationHours(seconds) {
    return (seconds / 3600).toFixed(2);
  }

  buildSummaryText(summary) {
    return [
      `Session duration: ${summary.sessionDuration} s (${this.formatDurationHours(summary.sessionDuration)} h)`,
      `Source frames: ${summary.sourceFrames}`,
      `Total sessions: ${summary.totalSessions}`,
    ].join("\n");
  }

  extractSummaryFromRow(item) {
    return {
      totalSessions:
        parseInt(item.TotalSessions, 10) ||
        parseInt(item.total_sessions, 10) ||
        0,
      sessionDuration: this._metricValue(
        item.SessionDuration || item.session_duration,
      ),
      sourceFrames: this._metricValue(item.SourceFrames || item.source_frames),
    };
  }

  handleSinceDateChange = (event) => {
    const sinceDate = event.target.value;
    this.setState({ sinceDate }, async () => {
      if (this.state.timeRange !== 6) return;

      const allCids = this.props.auth.cids;
      const selectedCids = this.state.selectedOption.map(
        (option) => allCids[option.value],
      );
      const data = await this.requestData(
        this.state.timeRange,
        new Date(),
        selectedCids,
        this.state.kind,
        this.state.selectedFlavor,
      );
      this.setState({ data, lastUpdated: new Date() });
    });
  };

  copySummaryToClipboard = () => {
    const text = this.state.data.summaryText || "";
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    }
  };
  componentDidMount() {
    this.props.getCids(this.props.auth.user.cid);
  }

  async componentWillReceiveProps(next) {
    const cids = next.auth.cids;
    let cidOption = cids.map((cid, index) => {
      return {
        value: index,
        label: cid.name,
      };
    });
    let selected = cidOption.filter((cid) => {
      return true;
    });
    if (selected.length > 0) {
      selected = selected.slice(0, 0);
    }

    const selectedCids = selected.map((option) => {
      return cids[option.value];
    });
    const data = await this.requestData(
      this.state.timeRange,
      new Date(),
      selectedCids,
      this.state.kind,
      this.state.selectedFlavor,
    );
    this.setState({
      data,
      cidOption,
      selectedOption: selected,
      lastUpdated: new Date(),
    });
  }

  handleChange = async (selectedOption) => {
    if (selectedOption && selectedOption.length > 10) {
      return;
    }

    const allCids = this.props.auth.cids;

    if (selectedOption) {
      const cids = selectedOption.map((option) => allCids[option.value]);

      if (cids) {
        const isOk = await this.checkSelectedCidsVersion(cids);

        if (!isOk) {
          this.setState({
            disableKindSwitch: true,
            errorMessage:
              "Cannot use Rung view: selected CID(s) still have packet version v1 within the last 2 months.",
          });
        } else {
          this.setState({
            disableKindSwitch: false,
            errorMessage: "",
          });
        }

        if (this.state.timeRange === 6 && !this.state.sinceDate) {
          this.setState({
            data: {
              labels: [],
              datasets: [],
              summaryText: "",
            },
            selectedOption: selectedOption || [],
          });
          return;
        }

        const data = await this.requestData(
          this.state.timeRange,
          new Date(),
          cids,
          this.state.kind,
          this.state.selectedFlavor,
        );

        this.setState({ data, selectedOption: selectedOption || [] });
      }
    } else {
      this.setState({
        data: {
          labels: [],
          datasets: [],
          summaryText: "",
        },
        selectedOption: [],
      });
    }
  };

  checkSelectedCidsVersion = async (selectedCids) => {
    for (const cid of selectedCids) {
      try {
        const res = await api.getPacketVersionByCid(cid.name);
        if (res.data.version === "v1") {
          return false; // Block if any CID still in v1
        }
      } catch (error) {
        console.error("Error checking packet version for CID", cid.name, error);
        return false; // Safer to block if error
      }
    }
    return true; // All good
  };

  handleChangeGroupBy = (event) => {
    const groupBy = parseInt(event.target.value);
    this.setState({ groupBy }, async () => {
      const allCids = this.props.auth.cids;
      const selectedCids = this.state.selectedOption.map(
        (option) => allCids[option.value],
      );
      const data = await this.requestData(
        this.state.timeRange,
        new Date(),
        selectedCids,
        this.state.kind,
        this.state.selectedFlavor,
      );
      this.setState({ data, lastUpdated: new Date() });
    });
  };
  handleFlavorChange = (event) => {
    const selectedFlavor = event.target.value;
    this.setState({ selectedFlavor }, async () => {
      const allCids = this.props.auth.cids;
      const selectedCids = this.state.selectedOption.map(
        (option) => allCids[option.value],
      );
      const data = await this.requestData(
        this.state.timeRange,
        new Date(),
        selectedCids,
        this.state.kind,
        this.state.selectedFlavor,
      );
      this.setState({ data, lastUpdated: new Date() });
    });
  };

  handleChangeTimeRange = (event) => {
    const timeRange = parseInt(event.target.value);
    this.setState({ timeRange }, async () => {
      if (timeRange === 6 && !this.state.sinceDate) {
        this.setState({
          data: {
            labels: [],
            datasets: [],
            summaryText: "",
          },
          lastUpdated: new Date(),
        });
        return;
      }

      const allCids = this.props.auth.cids;
      const selectedCids = this.state.selectedOption.map(
        (option) => allCids[option.value],
      );
      const data = await this.requestData(
        this.state.timeRange,
        new Date(),
        selectedCids,
        this.state.kind,
        this.state.selectedFlavor,
      );
      this.setState({ data, lastUpdated: new Date() });
    });
  };

  handleRefresh = async () => {
    if (this.state.timeRange === 6 && !this.state.sinceDate) {
      this.setState({
        data: {
          labels: [],
          datasets: [],
          summaryText: "",
        },
        lastUpdated: new Date(),
      });
      return;
    }

    const allCids = this.props.auth.cids;
    const selectedCids = this.state.selectedOption.map(
      (option) => allCids[option.value],
    );
    const data = await this.requestData(
      this.state.timeRange,
      new Date(),
      selectedCids,
      this.state.kind,
      this.state.selectedFlavor,
    );
    this.setState({
      data,
      lastUpdated: new Date(),
    });
  };

  toggleKind = async (event) => {
    const checked = event.target.checked; // true = want Rung, false = want Session
    const newKind = checked ? "rung" : "session";

    const allCids = this.props.auth.cids;
    const selectedCids = this.state.selectedOption.map(
      (option) => allCids[option.value],
    );

    if (newKind === "rung") {
      const isOk = await this.checkCidsAreV2(selectedCids);
      if (!isOk) {
        this.setState({
          errorMessage:
            "Cannot display by rung: some sessions are still using packet version v1.",
        });
        return; // Block switch!
      }
    }

    // If ok, proceed
    this.setState({ kind: newKind, errorMessage: "" }, () => {
      this.handleRefresh();
    });
  };

  checkCidsAreV2 = async (cids) => {
    for (const cid of cids) {
      try {
        const res = await api.getPacketVersionByCid(cid.name);
        if (res.data.version === "v1") {
          return false;
        }
      } catch (error) {
        console.error("Error checking packet version for CID", cid.name, error);
        return false;
      }
    }
    return true;
  };

  getFieldFromGroupBy(kind) {
    switch (this.state.groupBy) {
      case 0:
        return kind === "rung" ? "TotalRungs" : "TotalSessions";
      case 1:
        return "SessionDuration";
      case 2:
        return "SourceFrames";
      case 3:
        return "SourceDuration";
      case 4:
        return "MaxVCPU";
      case 5:
        return "TranscodedFrames";
      case 6:
        return "TranscodedDuration";
      default:
        return kind === "rung" ? "TotalRungs" : "TotalSessions";
    }
  }

  getTitleByGroupBy() {
    switch (this.state.groupBy) {
      case 0:
        return "Impressions for each customer ID";
      case 1:
        return "Session Durations (s) for each customer ID";
      case 2:
        return "Source Frames for each customer ID";
      case 3:
        return "Source Durations (s) for each customer ID";
      case 4:
        return "MaxVCPU for each customer ID";
      case 5:
        return "Transcoded Frames for each customer ID";
      case 6:
        return "Transcoded Durations (s) for each customer ID";
    }
  }

  fillData(data, field, labelEpochs) {
    const buckets = Array(labelEpochs.length).fill(0);
    if (!Array.isArray(data) || data.length === 0) return buckets;

    // indexe par TS (les API renvoient "TS" en secondes)
    const byTs = new Map();
    for (const item of data) {
      const ts = typeof item.TS === "number" ? item.TS : item.ts;
      if (typeof ts !== "number") continue;
      byTs.set(ts, this._metricValue(item[field]));
    }

    // re-projete sur chaque label (0 si manquant)
    return labelEpochs.map((ts) => byTs.get(ts) ?? 0);
  }

  _metricValue(value) {
    if (typeof value === "number") return value;
    if (value && typeof value === "object") {
      const entries = Object.entries(value);
      const allNumeric = entries.every(([k]) => !isNaN(parseInt(k, 10)));
      if (allNumeric) {
        // ex: tdr/tcfc/session_duration
        return entries.reduce((acc, [k, v]) => {
          const keyNum = parseInt(k, 10),
            valNum = parseInt(v, 10);
          return acc + (isNaN(keyNum) || isNaN(valNum) ? 0 : keyNum * valNum);
        }, 0);
      } else {
        // ex: Encoder/Platform/IP/Version -> somme des counts
        return entries.reduce((acc, [, v]) => acc + (parseInt(v, 10) || 0), 0);
      }
    }
    return 0;
  }

  // format d’affichage (Europe/Paris)
  formatTs(ts) {
    const ms = ts < 1e12 ? ts * 1000 : ts; // accepte sec ou ms en entrée
    return new Date(ms).toLocaleString("fr-FR", { timeZone: "Europe/Paris" });
  }

  async requestData(timeRange, lastUpdated, cids, kind, flavor) {
    const labelEpochs = this.getLabelEpochs(timeRange, lastUpdated);

    let newData = {
      labels: labelEpochs.map((ts) => this.formatTs(ts)),
      datasets: [],
      summaryText: "",
    };

    if (!labelEpochs.length) {
      return newData;
    }

    const summary = {
      sessionDuration: 0,
      sourceFrames: 0,
      totalSessions: 0,
    };

    for (let cid of cids) {
      let res = [];

      if (timeRange === 0) {
        res = await api.getLastHourChartByCid(cid.name, kind, flavor);
      } else {
        const startTs = labelEpochs[0];
        res = await api.getChartById(cid.name, kind, startTs, flavor);
      }

      if (res.error) {
        throw res.error;
      }

      (res.data || []).forEach((item) => {
        const rowSummary = this.extractSummaryFromRow(item);
        summary.sessionDuration += rowSummary.sessionDuration;
        summary.sourceFrames += rowSummary.sourceFrames;
        summary.totalSessions += rowSummary.totalSessions;
      });

      newData.datasets.push({
        label: cid.name,
        borderColor: colors[newData.datasets.length % colors.length],
        backgroundColor: colors[newData.datasets.length % colors.length],
        pointRadius: 1,
        fill: false,
        data: this.fillData(
          res.data,
          this.getFieldFromGroupBy(kind),
          labelEpochs,
        ),
      });
    }

    newData.summaryText = this.buildSummaryText(summary);
    return newData;
  }
  /*
  printDocument() {
    const input = document.getElementsByClassName('TrendChart');
    html2canvas(input)
      .then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF();
        pdf.addImage(imgData, 'JPEG', 0, 0);
        // pdf.output('dataurlnewwindow');
        pdf.save("download.pdf");
      })
    ;
  }*/

  render() {
    const { classes } = this.props;
    const { timeRange, groupBy, lastUpdated, data } = this.state;
    const allCids = this.props.auth.cids;
    const cids = this.state.selectedOption.map((option) => {
      return allCids[option.value];
    });
    return (
      <DashboardLayout title="Dashboard">
        <div className={classes.root}>
          <Grid container spacing={4}>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <Typography className={classes.title} variant="h4" gutterBottom>
                Customer ID Filter
              </Typography>
              <SelectBar
                styles={colourStyles}
                value={this.state.selectedOption}
                onChange={this.handleChange}
                options={this.state.cidOption}
                isMulti
              />
            </Grid>
            <Grid item lg={12} sm={12} xl={12} xs={12}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={this.state.kind === "rung"}
                      onChange={this.toggleKind}
                      color="primary"
                      disabled={this.state.disableKindSwitch}
                    />
                  }
                  label={
                    this.state.kind === "rung"
                      ? "View: Rungs"
                      : "View: Sessions"
                  }
                />
                {this.state.errorMessage && (
                  <Typography style={{ color: "red", marginTop: "10px" }}>
                    {this.state.errorMessage}
                  </Typography>
                )}
              </Grid>
              <FormControl style={{ minWidth: 220 }}>
                <InputLabel id="timeRange-label">Time Range</InputLabel>
                <Select
                  labelId="timeRange-label"
                  value={timeRange}
                  onChange={this.handleChangeTimeRange}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}>
                  {timeRangeList.map((label, index) => (
                    <MenuItem key={index} value={index}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {timeRange === 6 && (
                <TextField
                  label="Since date"
                  type="date"
                  value={this.state.sinceDate}
                  onChange={this.handleSinceDateChange}
                  InputLabelProps={{ shrink: true }}
                  style={{ marginLeft: 16, minWidth: 220 }}
                />
              )}
              <Select
                labelId="groupBy-label"
                value={groupBy}
                onChange={this.handleChangeGroupBy}
                label="Group By">
                {GroupByList.map((label, index) => (
                  <MenuItem key={index} value={index}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
              <Select
                labelId="flavor-label"
                id="flavor-select"
                value={this.state.selectedFlavor}
                onChange={this.handleFlavorChange}
                label="Flavor">
                {flavorOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <IconButton
                className={classes.refreshButton}
                onClick={this.handleRefresh}
                variant="text">
                <RefreshIcon />
              </IconButton>
            </Grid>
            {/*<div className="mb5">
              <button onClick={this.printDocument}>Print</button>
            </div>
           
            <Grid item lg={3} sm={6} xl={3} xs={12}>
              <Impressions cids={cids}/>
            </Grid>
            */}
            {!!this.state.selectedOption.length && !!data.summaryText && (
              <Grid item lg={12} sm={12} xl={12} xs={12}>
                <TextField
                  label="Summary to copy"
                  multiline
                  fullWidth
                  value={data.summaryText}
                  InputProps={{ readOnly: true }}
                  variant="outlined"
                />
                <Button
                  style={{ marginTop: 8 }}
                  variant="contained"
                  color="primary"
                  onClick={this.copySummaryToClipboard}>
                  Copy
                </Button>
              </Grid>
            )}
            <Grid item lg={12} md={12} xl={12} xs={12}>
              <TrendChart
                className={classes.item}
                lastUpdated={lastUpdated}
                cids={cids}
                timeRange={timeRange}
                data={data}
                title={this.getTitleByGroupBy()}
              />
            </Grid>
            <Grid item lg={12} md={12} xl={12} xs={12}>
              {
                <ImpressionChart
                  data={data}
                  timeRange={timeRange}
                  lastUpdated={lastUpdated}
                  cids={cids}
                />
              }
            </Grid>
          </Grid>
        </div>
      </DashboardLayout>
    );
  }
}

const mapStateToProps = (state) => ({
  auth: state.auth,
});

Dashboard.propTypes = {
  classes: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  getCids: PropTypes.func.isRequired,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, { getCids }),
)(Dashboard);
